import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = signed in
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    // Only check session on admin routes to avoid 401 noise on public pages.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
      refresh();
    } else {
      setUser(false);
      setChecking(false);
    }
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network errors on logout shouldn't block the UI from signing out.
      console.warn("Logout request failed:", err?.message || err);
    }
    setUser(false);
  }, []);

  // Memoize the context value so consumers don't re-render unless user/checking change.
  const value = useMemo(
    () => ({ user, checking, login, logout, refresh }),
    [user, checking, login, logout, refresh],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
